import '@fontsource/lato/300.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import 'bootstrap-icons/font/bootstrap-icons.scss';
import './assets/main.scss';
import 'intl-tel-input/build/css/intlTelInput.css';

import { createApp } from 'vue'
import App from './App.vue'

// Plugins
import router from './router'
import moment from 'moment'
import { createI18n } from 'vue-i18n'
import * as VueQuill from '@vueup/vue-quill'
import ImageResize from './assets/js/ResizeImage/ImageResize.js';
import { createHead, VueHeadMixin } from '@unhead/vue/client'

// Vertalingen
import langDe from './i18n/de'
import langEn from './i18n/en'
import langEs from './i18n/es'
import langFr from './i18n/fr'
import langIt from './i18n/it'
import langNl from './i18n/nl'
import langPl from './i18n/pl'
import langPt from './i18n/pt'

// Stores
import { createPinia } from 'pinia'
import { useCartStore } from './stores/cart.js'
import { useAccountStore } from './stores/account.js'
import { useOrganizationStore } from './stores/organization.js'
import { useToastsStore } from './stores/toasts.js'
import { initConsent } from './utils/cookieConsent.js'

const pinia = createPinia()

const app = createApp(App)
const head = createHead()

app.config.globalProperties.$moment = moment;

VueQuill.Quill.register('modules/imageResize', ImageResize);

app.component('QuillEditor', VueQuill.QuillEditor);

app.use(pinia);

app.use(router);

app.use(head)
app.mixin(VueHeadMixin)

let i18n = createI18n({
    locale: new Intl.Locale(navigator.language).language, // set locale
    fallbackLocale: 'en', // set fallback locale
    messageCompiler: {
        strict: true,
    },
    messages : {
        de : langDe,
        en : langEn,
        es : langEs,
        fr : langFr,
        it : langIt,
        nl : langNl,
        pl : langPl,
        pt : langPt,
    }, // set locale messages
});

app.use(i18n);

// The URL decides the language. Without this the app kept whatever
// navigator.language happened to be, so /nl rendered English for anyone whose
// browser was not Dutch - including the prerender service, which runs an
// en-US headless Chrome and produced English content under <html lang="nl">.
// That contradiction, on all eight locales, is the thing hreflang cannot
// survive. The initial locale above still comes from the browser, so it only
// applies before a /:lang route matches.
const URL_LOCALES = ['en', 'nl', 'de', 'es', 'fr', 'it', 'pl', 'pt'];

// i18n.global.locale is a plain string under the legacy API but a ref under
// the composition API, and this build sets __VUE_I18N_LEGACY_API__ to false.
// Assigning a string straight onto the ref silently does nothing, which is
// exactly how the language came to be ignored in the first place.
function setAppLocale(lang) {
    const locale = i18n.global.locale;
    if (locale && typeof locale === 'object' && 'value' in locale) {
        locale.value = lang;
    } else {
        i18n.global.locale = lang;
    }
}

router.beforeEach((to) => {
    const lang = to.params.lang;
    if (lang && URL_LOCALES.includes(lang)) {
        setAppLocale(lang);
    }
});

const cartStore = useCartStore();
const accountStore = useAccountStore();
const organizationStore = useOrganizationStore();
const toastsStore = useToastsStore();


(async () => {
    if(localStorage.getItem('token')) {
        // Note: deliberately not applying user.preferred_language here.
        //
        // The router's first navigation runs as soon as app.use(router) is
        // called, so the guard above has already set the locale from the URL
        // by the time this await resolves. Setting it again from the account
        // overrode that, which showed up as /nl rendering in English with the
        // switcher stuck on English - the URL correct, everything else not.
        //
        // preferred_language is applied where the user acts on it: at login
        // (Onboarding/Login.vue) and when saving it in the profile. From then
        // on the URL carries the language.
        await accountStore.validateAccountToken();
    }

    accountStore.getBetaFeatures();
    app.mount('#app');

    // Cookie consent (LA-159): re-apply a stored choice or show the banner.
    initConsent();
})();
